/** @format */

import { useState, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { uploadPdf } from "../api/papers";
import { importPdfProblem, type ImportPdfSlot } from "./classify-import-pdf";
import { readFirstPageText } from "./readPaperCover";
import { notify } from "./Toast/toast";
import { useBrand } from "../hooks/useBrand";

export type PdfSlotKey = "paper" | "markScheme" | "specification" | "paperCy" | "markSchemeCy";

export interface ImportPdfKeys {
	paper: string;
	markScheme: string;
	specification: string;
	paperCy?: string;
	markSchemeCy?: string;
}

interface PdfImportProps {
	onKeysChange: (keys: ImportPdfKeys | null, paperFile?: File | null) => void;
}

type LabelKey =
	| "pdfImport.paper"
	| "pdfImport.markScheme"
	| "pdfImport.specification"
	| "pdfImport.paperCy"
	| "pdfImport.markSchemeCy";

type LanguageKey = "pdfImport.english" | "pdfImport.welsh";

interface SlotDef {
	key: PdfSlotKey;
	labelKey: LabelKey;
	languageKey?: LanguageKey;
}

interface GroupDef {
	titleKey: "pdfImport.groupPaper" | "pdfImport.groupMarkScheme" | "pdfImport.groupSpecification";
	noteKey?: "pdfImport.specificationNote";
	slots: SlotDef[];
}

const MAX_PDF_BYTES = 10 * 1024 * 1024;

type SlotState = {
	file?: File;
	assetKey?: string;
	error?: string;
	accepted: boolean;
	status: "idle" | "checking" | "uploading" | "success" | "error";
};

const emptySlot = (): SlotState => ({ status: "idle", accepted: false });

interface PdfDropSlotProps {
	label: string;
	language?: string;
	slot: SlotState;
	isDisabled: boolean;
	onFile: (file: File) => void;
	onReject: (message: string) => void;
	onRemove: () => void;
}

const PdfDropSlot = ({ label, language, slot, isDisabled, onFile, onReject, onRemove }: PdfDropSlotProps) => {
	const { t } = useTranslation();
	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		accept: { "application/pdf": [".pdf"] },
		maxSize: MAX_PDF_BYTES,
		maxFiles: 1,
		disabled: isDisabled,
		onDrop: (files) => {
			const file = files[0];
			if (file) {
				onFile(file);
			}
		},
		onDropRejected: (rejections) => {
			const code = rejections[0]?.errors[0]?.code;
			if (code === "file-too-large") {
				onReject(t("pdfImport.fileTooLarge"));
				return;
			}
			if (code === "too-many-files") {
				onReject(t("pdfImport.oneFile"));
				return;
			}
			onReject(t("pdfImport.fileType", { type: rejections[0]?.file.type || t("pdfImport.unknownType") }));
		},
	});
	const zoneClass = [
		"border border-dashed rounded-sm w-full h-80 flex items-center justify-center mt-2",
		isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
		slot.error ? "border-red-600" : isDragActive ? "border-(--admin-color-primary-strong)" : "border-gray-400",
	].join(" ");

	return (
		<div>
			{language && <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">{language}</p>}
			{slot.error && (
				<p className="text-red-600 font-semibold mb-2" role="alert">
					{slot.error}
				</p>
			)}
			{slot.file ? (
				<div>
					<div className="flex flex-row gap-3 items-center justify-center border border-slate-200 rounded-lg px-3 py-2 mb-2 shadow-(--admin-shadow-raised)">
						<p className="min-w-0 flex-1 truncate text-(--admin-color-primary-strong) font-semibold">
							{slot.file.name}
						</p>
						<button
							aria-label={`${t("pdfImport.remove")} ${label}`}
							type="button"
							onClick={onRemove}
							disabled={isDisabled}
							className="cursor-pointer rounded-md bg-red-600 w-24 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 ml-auto disabled:opacity-60"
						>
							{t("pdfImport.remove")}
						</button>
					</div>
					{slot.status === "checking" && (
						<p className="text-sm text-slate-600" role="status">
							{t("pdfImport.checking")}
						</p>
					)}
					{slot.accepted && slot.status !== "uploading" && slot.status !== "success" && (
						<p className="text-sm font-medium text-green-800" role="status">
							{t("pdfImport.ready")}
						</p>
					)}
					{slot.status === "uploading" && <p className="text-sm text-slate-600">{t("pdfImport.uploading")}</p>}
					{slot.status === "success" && <p className="text-sm font-medium text-green-800">{t("pdfImport.uploaded")}</p>}
				</div>
			) : (
				<div {...getRootProps({ className: zoneClass })}>
					<input {...getInputProps({ "aria-label": label })} />
					<div className="flex flex-col gap-2 items-center justify-center px-4 text-center">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							className="h-10 w-10 text-gray-400"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
							/>
						</svg>
						<p className="text-gray-400 font-semibold">{t("pdfImport.clickOrDrop")}</p>
						<p className="text-gray-400 text-sm font-semibold">{t("pdfImport.pdfFormat")}</p>
					</div>
				</div>
			)}
		</div>
	);
};

export const PdfImport = ({ onKeysChange }: PdfImportProps) => {
	const { brand } = useBrand();
	const { t } = useTranslation();
	const canShowWelsh = brand === "wjec";
	const groups: GroupDef[] = [
		{
			titleKey: "pdfImport.groupPaper",
			slots: [
				{ key: "paper", labelKey: "pdfImport.paper", languageKey: "pdfImport.english" },
				...(canShowWelsh
					? [
							{
								key: "paperCy" as const,
								labelKey: "pdfImport.paperCy" as const,
								languageKey: "pdfImport.welsh" as const,
							},
						]
					: []),
			],
		},
		{
			titleKey: "pdfImport.groupMarkScheme",
			slots: [
				{ key: "markScheme", labelKey: "pdfImport.markScheme", languageKey: "pdfImport.english" },
				...(canShowWelsh
					? [
							{
								key: "markSchemeCy" as const,
								labelKey: "pdfImport.markSchemeCy" as const,
								languageKey: "pdfImport.welsh" as const,
							},
						]
					: []),
			],
		},
		{
			titleKey: "pdfImport.groupSpecification",
			noteKey: "pdfImport.specificationNote",
			slots: [{ key: "specification", labelKey: "pdfImport.specification" }],
		},
	];
	const visibleSlots = groups.flatMap((group) => group.slots);
	const [slots, setSlots] = useState<Record<PdfSlotKey, SlotState>>({
		paper: emptySlot(),
		markScheme: emptySlot(),
		specification: emptySlot(),
		paperCy: emptySlot(),
		markSchemeCy: emptySlot(),
	});
	const checks = useRef<Partial<Record<PdfSlotKey, number>>>({});

	const updateSlot = (key: PdfSlotKey, patch: Partial<SlotState>) =>
		setSlots((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

	const confirmFile = (key: ImportPdfSlot, file: File) => {
		const check = (checks.current[key] ?? 0) + 1;
		checks.current[key] = check;
		setSlots((prev) => ({
			...prev,
			[key]: { status: "checking", file, accepted: false, error: undefined },
		}));
		void readFirstPageText(file).then((text) => {
			if (checks.current[key] !== check) {
				return;
			}
			const problem = importPdfProblem(text ?? "", key);
			setSlots((prev) => ({
				...prev,
				[key]: {
					...prev[key],
					status: "idle",
					file,
					accepted: problem === null,
					error: problem ? t(problem) : undefined,
				},
			}));
		});
	};

	const rejectFile = (key: PdfSlotKey, message: string) => {
		checks.current[key] = (checks.current[key] ?? 0) + 1;
		setSlots((prev) => ({ ...prev, [key]: { ...emptySlot(), error: message } }));
	};

	const removePdf = (key: PdfSlotKey) => {
		checks.current[key] = (checks.current[key] ?? 0) + 1;
		setSlots((prev) => ({ ...prev, [key]: emptySlot() }));
		onKeysChange(null, null);
	};

	const handleUpload = async () => {
		const pending = visibleSlots.filter(({ key }) => slots[key].status !== "success");
		const results = await Promise.all(
			pending.map(async ({ key }) => {
				const file = slots[key].file;
				if (!file) {
					return { key, assetKey: undefined };
				}
				updateSlot(key, { status: "uploading", error: undefined });
				try {
					const assetKey = await uploadPdf(brand, file);
					updateSlot(key, { status: "success", assetKey });
					return { key, assetKey };
				} catch (err) {
					const message = err instanceof Error && err.message ? err.message : t("pdfImport.uploadFailed");
					updateSlot(key, { status: "error", error: message });
					notify("error", message);
					return { key, assetKey: undefined };
				}
			}),
		);

		const keys: Partial<ImportPdfKeys> = {};
		visibleSlots.forEach(({ key }) => {
			keys[key] = slots[key].assetKey;
		});
		results.forEach(({ key, assetKey }) => {
			if (assetKey) {
				keys[key] = assetKey;
			}
		});
		if (visibleSlots.every(({ key }) => keys[key])) {
			onKeysChange(keys as ImportPdfKeys, slots.paper.file ?? null);
		}
	};

	const isAllSelected = visibleSlots.every(({ key }) => slots[key].accepted);
	const isUploading = visibleSlots.some(({ key }) => slots[key].status === "uploading");
	const isAllUploaded = visibleSlots.every(({ key }) => slots[key].status === "success");
	const readyCount = visibleSlots.filter(({ key }) => slots[key].accepted).length;

	return (
		<div className="max-w-4xl">
			<p className="mt-1 mb-4 text-sm text-slate-600">{t("pdfImport.intro")}</p>

			<div className="flex flex-col gap-4">
				{groups.map((group) => (
					<section key={group.titleKey} className="rounded-lg border border-slate-200 bg-white p-4">
						<h3 className="text-base font-semibold text-(--admin-color-primary)">{t(group.titleKey)}</h3>
						{group.noteKey && <p className="mt-1 text-sm text-slate-600">{t(group.noteKey)}</p>}
						<div className={group.slots.length > 1 ? "mt-4 grid gap-4 md:grid-cols-2" : "mt-4 max-w-md"}>
							{group.slots.map((slotDef) => (
								<PdfDropSlot
									key={slotDef.key}
									label={t(slotDef.labelKey)}
									language={group.slots.length > 1 && slotDef.languageKey ? t(slotDef.languageKey) : undefined}
									slot={slots[slotDef.key]}
									isDisabled={isUploading}
									onFile={(file) => confirmFile(slotDef.key, file)}
									onReject={(message) => rejectFile(slotDef.key, message)}
									onRemove={() => removePdf(slotDef.key)}
								/>
							))}
						</div>
					</section>
				))}
			</div>

			<div className="mt-4 flex flex-wrap items-center justify-between gap-3">
				<p className="text-sm text-slate-600">
					<span>{readyCount}</span> {t("pdfImport.of")} <span>{visibleSlots.length}</span> {t("pdfImport.filesReady")}
				</p>
				<button
					type="button"
					onClick={() => void handleUpload()}
					disabled={!isAllSelected || isUploading || isAllUploaded}
					className="rounded-md bg-(--admin-color-primary-strong) px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 cursor-pointer disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--admin-color-primary-strong)"
				>
					{isUploading ? t("pdfImport.uploading") : t("pdfImport.uploadAll")}
				</button>
			</div>
		</div>
	);
};
